import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSocket } from "../socket";
import { usePageFocus } from "../pagefocus";
import { GameSocketMessageEventTypes, GameSocketNotifyMessageTypes, IGameSocketNotifyMessage } from "../../../src/models/game/socket";
import { Intent, OverlayToaster, Position } from "@blueprintjs/core";
import NotificationComponent from "./notificationcomponent";
import styles from './notifications.module.css';
import { GameNotificationBlockType } from "@/src/server/models/game/modules/notifications/blocks";

type NotificationContextProps = {
    // used to add additional styling to the toasts
    // for example, we can offset toasts from the bottom
    // when the game play view is up
    className?: string;
    setClassName: Dispatch<SetStateAction<string | undefined>>
}

const NotificationContext = createContext<NotificationContextProps>(null);

type NotificationsProps = {
    children?: React.ReactNode;
}

export function Notifications(
    {
        children,
    }: NotificationsProps,
): React.ReactNode {
    const toasterRef = useRef<OverlayToaster>();
    const { socket } = useSocket();
    const isPageFocused = usePageFocus();
    const [notifications, setNotifications] = useState<IGameSocketNotifyMessage[]>([]);
    const [className, setClassName] = useState<string | undefined>(undefined);

    useEffect(
        () => {
            if (!isPageFocused || notifications.length === 0) {
                return;
            }

            for (const message of notifications) {
                const key = `notif_${message.gameId}_${Math.random()}`;

                toasterRef.current.show(
                    {
                        message: <NotificationComponent
                            message={message}
                            onClose={() => toasterRef.current.dismiss(key)}
                        />,
                        className: styles.notification,
                        intent: Intent.PRIMARY,
                    },
                    key,
                );
            }

            setNotifications([]);
        },
        [notifications, isPageFocused, setNotifications],
    );

    const createToast = useCallback(
        (message: IGameSocketNotifyMessage): void => {
            setNotifications(
                (oldNotifications) => [
                    ...oldNotifications,
                    {
                        ...message,
                        ts: Date.now(),
                    },
                ],
            );
        },
        [setNotifications],
    );

    // leaving this in for easy testing
    /*
    useEffect(
        () => {
            const data: IGameSocketNotifyMessage = {
                "type": GameSocketNotifyMessageTypes.Notification,
                "gameId": 1498,
                "data": {
                    "key": "GameNotificationInvite",
                    "titleKey": "GameNotificationInviteTitle",
                    "blocks": {
                        "game": {
                            "type": GameNotificationBlockType.Game,
                            "game": {
                                "id": 1498,
                                "name": "other track",
                                "map": {
                                    "id": 1,
                                    "name": "World"
                                }
                            }
                        },
                        "inviter": {
                            "type": GameNotificationBlockType.User,
                            "user": {
                                "id": 4,
                                "name": "porty",
                                "avatar": "110_HeadFront.png"
                            }
                        }
                    }
                }
            };

            const onKey = () => {
                createToast(data);
            };

            window.onkeydown = (e) => {
                if (e.key === 'n') {
                    onKey();
                }
            };

            return () => {
                window.onkeydown = null;
            };
        },
        [createToast],
    );
    */

    useEffect(
        () => {
            if (!socket) {
                return;
            }

            const onActiveGameChange = (message: IGameSocketNotifyMessage) => {
                switch (message.type) {
                    case GameSocketNotifyMessageTypes.Notification:
                        createToast(message);

                        break;
                }
            };

            socket.on(GameSocketMessageEventTypes.Notify, onActiveGameChange);

            return () => {
                socket.off(GameSocketMessageEventTypes.Notify, onActiveGameChange);
            };
        },
        [createToast, socket],
    );

    const value = useMemo(
        (): NotificationContextProps => ({
            className,
            setClassName,
        }),
        [className, setClassName],
    );

    return (
        <NotificationContext.Provider value={value}>
            <OverlayToaster
                className={className}
                ref={toasterRef}
                position={Position.BOTTOM_RIGHT}
                maxToasts={3}
                usePortal={false}
            />
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationContext);
}
